import { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getVehicleTypes } from "@/redux/search/searchSlice";

export const BIKE_GROUP = [
  "bike",
  "motorbike",
  "electric scooter",
  "electric bike",
  "scooter",
];

const DEFAULT_VEHICLE_TYPES = [...BIKE_GROUP, "car"];

export default function useVehicleToggle({ syncToRedux = true, initialTypes = null } = {}) {
  const dispatch = useDispatch();
  const rawVehicleType = useSelector((state) => state.search.vehicleType);
  const rehydrated = useSelector((state) => state.search?._persist?.rehydrated);
  const vehicleType = Array.isArray(rawVehicleType) ? rawVehicleType : DEFAULT_VEHICLE_TYPES;
  const [selectedItems, setSelectedItems] = useState(initialTypes || vehicleType);
  const [prevVehicleType, setPrevVehicleType] = useState(vehicleType);

  if (vehicleType !== prevVehicleType) {
    setPrevVehicleType(vehicleType);
    setSelectedItems(vehicleType);
  }

  // Re-applied once after rehydration: a stored session selection would otherwise override the page's own vehicle type.
  const appliedInitial = useRef(false);
  useEffect(() => {
    if (!initialTypes || !rehydrated || appliedInitial.current) return;
    appliedInitial.current = true;
    setSelectedItems(initialTypes);
  }, [initialTypes, rehydrated]);

  useEffect(() => {
    if (syncToRedux) {
      dispatch(getVehicleTypes(selectedItems));
    }
  }, [dispatch, selectedItems, syncToRedux]);

  const toggleSelection = (item) => {
    setSelectedItems((prevSelected) => {
      const isSelected = prevSelected.includes(item);
      let newSelection;

      if (item === "bike") {
        if (isSelected) {
          newSelection = prevSelected.filter((i) => !BIKE_GROUP.includes(i));
        } else {
          newSelection = [...new Set([...prevSelected, ...BIKE_GROUP])];
        }
      } else if (item === "car") {
        if (isSelected) {
          newSelection = prevSelected.filter((i) => i !== "car");
        } else {
          newSelection = [...prevSelected, "car"];
        }
      } else {
        return prevSelected;
      }

      const includesBikeGroup = newSelection.some((i) =>
        BIKE_GROUP.includes(i)
      );
      const includesCar = newSelection.includes("car");

      if (!includesBikeGroup && !includesCar) {
        return prevSelected;
      }

      return newSelection;
    });
  };

  return { selectedItems, toggleSelection };
}

